import React from "react";
import { Box, CircularProgress } from "@mui/material";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY } from "../../utils/designTokens";

// Pieces shared by the Settings page's sections (screens/Settings).

/** A section: small uppercase heading, then its card(s). */
export function SettingsSection({ id, title, children }) {
  return (
    <Box component="section" aria-labelledby={id} sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      <Box component="h2" id={id} sx={{ m: 0, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: C.muted }}>
        {title}
      </Box>
      {children}
    </Box>
  );
}

export function SettingsCard({ children, sx }) {
  return <Box sx={{ bgcolor: C.bg, border: `1px solid ${C.border}`, borderRadius: "20px", overflow: "hidden", ...sx }}>{children}</Box>;
}

/** 48px rounded icon tile, red on the soft red tint. */
export function IconTile({ children }) {
  return (
    <Box aria-hidden="true" sx={{ width: 48, height: 48, flexShrink: 0, borderRadius: "14px", bgcolor: C.redTint, color: C.red, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 24 } }}>
      {children}
    </Box>
  );
}

/** The row's title and the line under it. */
export function RowText({ titleId, title, sub }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, fontFamily: FONT_BODY }}>
      <Box id={titleId} sx={{ fontSize: 16, fontWeight: 700, color: C.ink, lineHeight: 1.3 }}>
        {title}
      </Box>
      {sub && <Box sx={{ fontSize: 14, color: C.muted, mt: 0.25, lineHeight: 1.45 }}>{sub}</Box>}
    </Box>
  );
}

const OFF = "#B9B5AD";

/** A 52×30 on/off switch: role="switch", labelled by `labelledBy`. */
export function Switch({ checked, onChange, labelledBy, describedBy, disabled = false, busy = false }) {
  return (
    <Box
      component="button"
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      aria-disabled={disabled || undefined}
      aria-busy={busy || undefined}
      onClick={() => !disabled && !busy && onChange(!checked)}
      sx={{
        ...BUTTON_RESET,
        ...FOCUS_RING,
        position: "relative",
        flexShrink: 0,
        width: 52,
        height: 30,
        borderRadius: "999px",
        bgcolor: checked ? C.green : OFF,
        opacity: disabled ? 0.55 : 1,
        cursor: disabled || busy ? "default" : "pointer",
        transition: "background-color .18s ease",
      }}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          position: "absolute",
          top: 3,
          left: 3,
          width: 24,
          height: 24,
          borderRadius: "50%",
          bgcolor: "#FFFFFF",
          boxShadow: "0 1px 3px rgba(26,25,24,0.3)",
          transform: checked ? "translateX(22px)" : "translateX(0)",
          transition: "transform .18s ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      >
        {busy && <CircularProgress size={14} thickness={6} sx={{ color: checked ? C.green : C.muted }} />}
      </Box>
    </Box>
  );
}
